import { useEffect, useState } from "react";
import { api, formatMoney, LOCATION_LABEL } from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "@/components/ui/select";
import { ArrowDownRight, ArrowUpRight, Plus, Minus, ArrowLeftRight } from "lucide-react";
import StockMovementDialog from "@/components/StockMovementDialog";
import { format } from "date-fns";

export default function Transactions() {
    const [txs, setTxs] = useState([]);
    const [products, setProducts] = useState([]);
    const [filter, setFilter] = useState("all");
    const [movement, setMovement] = useState({ open: false, type: "IN" });

    const load = async () => {
        const [t, p] = await Promise.all([
            api.get("/transactions"),
            api.get("/products"),
        ]);
        setTxs(t.data);
        setProducts(p.data);
    };

    useEffect(() => {
        load();
    }, []);

    const filtered = txs.filter((tx) => filter === "all" || tx.type === filter);

    return (
        <>
            <PageHeader
                title="Transactions"
                subtitle="Historique complet des entrées et sorties"
                actions={
                    <>
                        <Button
                            data-testid="tx-new-in-btn"
                            onClick={() => setMovement({ open: true, type: "IN" })}
                            className="bg-emerald-600 hover:bg-emerald-700 text-white"
                        >
                            <Plus className="w-4 h-4 mr-2" />
                            Entrée
                        </Button>
                        <Button
                            data-testid="tx-new-out-btn"
                            onClick={() => setMovement({ open: true, type: "OUT" })}
                            variant="outline"
                            className="border-rose-200 text-rose-700 hover:bg-rose-50 hover:text-rose-800"
                        >
                            <Minus className="w-4 h-4 mr-2" />
                            Sortie
                        </Button>
                    </>
                }
            />

            <div className="p-8 space-y-6">
                <div className="flex gap-3">
                    <Select value={filter} onValueChange={setFilter}>
                        <SelectTrigger
                            data-testid="tx-filter"
                            className="w-56 h-11 bg-white"
                        >
                            <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                            <SelectItem value="all">Toutes les transactions</SelectItem>
                            <SelectItem value="IN">Entrées uniquement</SelectItem>
                            <SelectItem value="OUT">Sorties uniquement</SelectItem>
                        </SelectContent>
                    </Select>
                </div>

                <Card className="border-slate-200 shadow-sm overflow-hidden">
                    <Table>
                        <TableHeader className="bg-slate-50">
                            <TableRow>
                                <TableHead className="text-slate-600">Date</TableHead>
                                <TableHead className="text-slate-600">Type</TableHead>
                                <TableHead className="text-slate-600">Produit</TableHead>
                                <TableHead className="text-slate-600">Emplacement</TableHead>
                                <TableHead className="text-right text-slate-600">Qté</TableHead>
                                <TableHead className="text-right text-slate-600">
                                    Prix unitaire
                                </TableHead>
                                <TableHead className="text-right text-slate-600">Total</TableHead>
                                <TableHead className="text-slate-600">Note</TableHead>
                            </TableRow>
                        </TableHeader>
                        <TableBody>
                            {filtered.length === 0 && (
                                <TableRow>
                                    <TableCell
                                        colSpan={8}
                                        className="text-center text-slate-500 py-10"
                                    >
                                        Aucune transaction
                                    </TableCell>
                                </TableRow>
                            )}
                            {filtered.map((tx) => {
                                const isVault =
                                    typeof tx.product_id === "string" &&
                                    tx.product_id.startsWith("vault-");
                                const isTransfer = tx.type === "TRANSFER";
                                const isMoneyIn = isTransfer
                                    ? false
                                    : isVault
                                      ? tx.type === "IN"
                                      : tx.type === "OUT";
                                const sign = isTransfer ? "" : isMoneyIn ? "+" : "−";
                                const totalColor = isTransfer
                                    ? "text-sky-700"
                                    : isMoneyIn
                                      ? "text-emerald-700"
                                      : "text-rose-700";
                                return (
                                <TableRow
                                    key={tx.id}
                                    data-testid={`tx-row-${tx.id}`}
                                    className="hover:bg-slate-50"
                                >
                                    <TableCell className="text-slate-600 text-sm whitespace-nowrap">
                                        {format(new Date(tx.created_at), "dd/MM/yyyy HH:mm")}
                                    </TableCell>
                                    <TableCell>
                                        <Badge
                                            className={
                                                tx.type === "IN"
                                                    ? "bg-emerald-100 text-emerald-800 border-emerald-200"
                                                    : tx.type === "TRANSFER"
                                                      ? "bg-sky-100 text-sky-800 border-sky-200"
                                                      : "bg-rose-100 text-rose-800 border-rose-200"
                                            }
                                        >
                                            {tx.type === "IN" ? (
                                                <ArrowDownRight className="w-3 h-3 mr-1" />
                                            ) : tx.type === "TRANSFER" ? (
                                                <ArrowLeftRight className="w-3 h-3 mr-1" />
                                            ) : (
                                                <ArrowUpRight className="w-3 h-3 mr-1" />
                                            )}
                                            {tx.type === "IN"
                                                ? "Entrée"
                                                : tx.type === "TRANSFER"
                                                  ? "Transfert"
                                                  : "Sortie"}
                                        </Badge>
                                    </TableCell>
                                    <TableCell className="font-medium text-slate-900">
                                        {tx.product_name}
                                    </TableCell>
                                    <TableCell className="text-slate-600 text-sm">
                                        {tx.type === "TRANSFER"
                                            ? `${LOCATION_LABEL[tx.location] || "—"} → ${LOCATION_LABEL[tx.to_location] || "—"}`
                                            : LOCATION_LABEL[tx.location] || "—"}
                                    </TableCell>
                                    <TableCell className="text-right tabular-nums">
                                        {tx.quantity}
                                    </TableCell>
                                    <TableCell className="text-right tabular-nums">
                                        {formatMoney(tx.unit_price)}
                                    </TableCell>
                                    <TableCell
                                        className={`text-right tabular-nums font-semibold ${totalColor}`}
                                    >
                                        {sign}
                                        {formatMoney(tx.total)}
                                    </TableCell>
                                    <TableCell className="text-slate-500 text-sm truncate max-w-xs">
                                        {tx.note}
                                    </TableCell>
                                </TableRow>
                                );
                            })}
                        </TableBody>
                    </Table>
                </Card>
            </div>

            <StockMovementDialog
                open={movement.open}
                onOpenChange={(v) => setMovement((m) => ({ ...m, open: v }))}
                type={movement.type}
                products={products}
                onDone={load}
            />
        </>
    );
}
